/* eslint-disable @typescript-eslint/naming-convention */
import { A, NUMPAD_NINE, NUMPAD_ZERO, P } from '@angular/cdk/keycodes';
import {
   ChangeDetectionStrategy,
   Component,
   EventEmitter,
   Inject,
   Input,
   OnInit,
   Output,
   ViewEncapsulation,
} from '@angular/core';

import { LcsDateTimeAdapter } from '../configuration/date-time-adapter';
import { LCS_DATE_TIME_FORMATS, LcsDateTimeFormats } from '../configuration/date-time-formats';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'lcs-time-picker',
    templateUrl: 'time-picker.component.html',
    styleUrls: ['time-picker.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LcsTimePickerComponent<D> implements OnInit {
   /** A date representing the period (month or year) to start the calendar in. */
   @Input()
   get startAt(): D | null {
      return this._startAt;
   }

   set startAt(value: D | null) {
      this._startAt = this.getValidDateOrNull(this.dateTimeAdapter.deserialize(value));
   }

   private _startAt: D | null;

   @Input() hourPagingInterval = 3;

   @Input() minutePagingInterval = 5;

   @Output() selectedChange = new EventEmitter<D>();

   set activeDate(date: D | null) {
      this._activeDate = date;
      if (date) {
         this.currentTime = this.dateTimeAdapter.format(date, this.dateTimeFormats.display.timeA11yLabel);

         const formattedHours = this.dateTimeAdapter.formatHours(date, this.dateTimeFormats.display.hours);

         this.hours = this.dateTimeAdapter.getHours(date);
         this.hoursDisplay = formattedHours.hours;
         this.hoursText = (this.hours > 12 ? this.hours - 12 : this.hours).toString();
         this.minutes = this.dateTimeAdapter.getMinutes(date);
         this.minutesText = this.dateTimeAdapter.formatMinutes(date, this.dateTimeFormats.display.minutes);
         this.period = formattedHours.dayPeriod;
         this.opposingPeriod = this.dateTimeAdapter.getOpposingPeriod(date, this.dateTimeFormats.display.hours);
      } else {
         this.currentTime = null;
         this.hours = null;
         this.hoursText = null;
         this.hoursDisplay = null;
         this.minutes = null;
         this.minutesText = null;
         this.period = null;
         this.opposingPeriod = null;
      }
   }

   get activeDate(): D | null {
      return this._activeDate;
   }

   private _activeDate: D | null;

   currentTime: string | null = null;

   hours: number | null = null;

   hoursText: string | null = null;

   hoursDisplay: string | null = null;

   minutes: number | null = null;

   minutesText: string | null = null;

   minutesDisplay: number | null = null;

   period: string | null = null;

   opposingPeriod: string | null = null;

   constructor(
      private dateTimeAdapter: LcsDateTimeAdapter<D>,
      @Inject(LCS_DATE_TIME_FORMATS) private dateTimeFormats: LcsDateTimeFormats
   ) {}

   ngOnInit() {
      this.activeDate = this.startAt || this.dateTimeAdapter.today();
   }

   onIncrementHours(): void {
      if (this._activeDate) {
         this.activeDate = this.dateTimeAdapter.addHours(this._activeDate, 1);
         this.selectedChange.emit(this._activeDate);
      }
   }

   onDecrementHours(): void {
      if (this._activeDate) {
         this.activeDate = this.dateTimeAdapter.addHours(this._activeDate, -1);
         this.selectedChange.emit(this._activeDate);
      }
   }

   onPageUpHours(): void {
      if (this._activeDate) {
         this.activeDate = this.dateTimeAdapter.addHours(this._activeDate, this.hourPagingInterval);
         this.selectedChange.emit(this._activeDate);
      }
   }

   onPageDownHours(): void {
      if (this._activeDate) {
         this.activeDate = this.dateTimeAdapter.addHours(this._activeDate, -this.hourPagingInterval);
         this.selectedChange.emit(this._activeDate);
      }
   }

   onHomeHours(): void {
      if (this._activeDate) {
         const currentHours = this.dateTimeAdapter.getHours(this._activeDate);

         let hoursToSubtract: number;

         if (currentHours > 12) {
            hoursToSubtract = currentHours - 13;
         } else if (currentHours === 12) {
            hoursToSubtract = -1;
         } else {
            hoursToSubtract = currentHours - 1;
         }

         if (hoursToSubtract) {
            this.activeDate = this.dateTimeAdapter.addHours(this._activeDate, -hoursToSubtract);
            this.selectedChange.emit(this._activeDate);
         }
      }
   }

   onEndHours(): void {
      if (this._activeDate) {
         const currentHours = this.dateTimeAdapter.getHours(this._activeDate);

         let hoursToAdd: number;

         if (currentHours > 12) {
            hoursToAdd = 12 - currentHours;
         } else if (currentHours === 12) {
            hoursToAdd = 0;
         } else {
            hoursToAdd = -currentHours;
         }

         if (hoursToAdd) {
            this.activeDate = this.dateTimeAdapter.addHours(this._activeDate, hoursToAdd);
            this.selectedChange.emit(this._activeDate);
         }
      }
   }

   onIncrementMinutes(): void {
      if (this._activeDate) {
         this.activeDate = this.dateTimeAdapter.addMinutes(this._activeDate, 1);
         this.selectedChange.emit(this._activeDate);
      }
   }

   onDecrementMinutes(): void {
      if (this._activeDate) {
         this.activeDate = this.dateTimeAdapter.addMinutes(this._activeDate, -1);
         this.selectedChange.emit(this._activeDate);
      }
   }

   onPageUpMinutes(): void {
      if (this._activeDate) {
         this.activeDate = this.dateTimeAdapter.addMinutes(this._activeDate, this.minutePagingInterval);
         this.selectedChange.emit(this._activeDate);
      }
   }

   onPageDownMinutes(): void {
      if (this._activeDate) {
         this.activeDate = this.dateTimeAdapter.addMinutes(this._activeDate, -this.minutePagingInterval);
         this.selectedChange.emit(this._activeDate);
      }
   }

   onHomeMinutes(): void {
      if (this._activeDate) {
         const minutesToSubtract = this.dateTimeAdapter.getMinutes(this._activeDate);
         this.activeDate = this.dateTimeAdapter.addMinutes(this._activeDate, -minutesToSubtract);
         this.selectedChange.emit(this._activeDate);
      }
   }

   onEndMinutes(): void {
      if (this._activeDate) {
         const currentMinutes = this.dateTimeAdapter.getMinutes(this._activeDate);
         const minutesToAdd = 59 - currentMinutes;
         this.activeDate = this.dateTimeAdapter.addMinutes(this._activeDate, minutesToAdd);
         this.selectedChange.emit(this._activeDate);
      }
   }

   onChangePeriod(): void {
      if (this._activeDate) {
         if (this.dateTimeAdapter.getHours(this._activeDate) >= 12) {
            this.activeDate = this.dateTimeAdapter.addHours(this._activeDate, -12);
         } else {
            this.activeDate = this.dateTimeAdapter.addHours(this._activeDate, 12);
         }
         this.selectedChange.emit(this._activeDate);
      }
   }

   onHomePeriod(): void {
      if (this._activeDate && this.dateTimeAdapter.getHours(this._activeDate) >= 12) {
         this.activeDate = this.dateTimeAdapter.addHours(this._activeDate, -12);
         this.selectedChange.emit(this._activeDate);
      }
   }

   onEndPeriod(): void {
      if (this._activeDate && this.dateTimeAdapter.getHours(this._activeDate) < 12) {
         this.activeDate = this.dateTimeAdapter.addHours(this._activeDate, 12);
         this.selectedChange.emit(this._activeDate);
      }
   }

   onHourKeydown(event: KeyboardEvent): void {
      const number = this.getNumberFromKeyCode(event.keyCode);
      if (this._activeDate && !isNaN(number)) {
         const currentHours = this.dateTimeAdapter.getHours(this._activeDate);
         const normalizedCurrentHours = currentHours > 12 ? currentHours - 12 : currentHours;

         let newHour: number;
         if (normalizedCurrentHours === 1 && number < 3) {
            newHour = 10 + number;
         } else {
            newHour = number;
         }

         const hoursToAdd = newHour - normalizedCurrentHours;

         if (newHour && hoursToAdd) {
            this.activeDate = this.dateTimeAdapter.addHours(this._activeDate, hoursToAdd);
            this.selectedChange.emit(this._activeDate);
         }
      }
   }

   onMinuteKeydown(event: KeyboardEvent): void {
      const number = this.getNumberFromKeyCode(event.keyCode);
      if (this._activeDate && !isNaN(number)) {
         const currentMinutes = this.dateTimeAdapter.getMinutes(this._activeDate);

         let minutesToAdd: number;

         if (currentMinutes > 5) {
            minutesToAdd = number - currentMinutes;
         } else {
            // e.g., to go from 3 to 35, 3 * 10 + 5 - 3 = 32, and 32 + 3 = 35
            minutesToAdd = currentMinutes * 10 + number - currentMinutes;
         }

         if (minutesToAdd) {
            this.activeDate = this.dateTimeAdapter.addMinutes(this._activeDate, minutesToAdd);
            this.selectedChange.emit(this._activeDate);
         }
      }
   }

   onPeriodKeydown(event: KeyboardEvent): void {
      if (!this._activeDate) {
         return;
      }

      const keyCode = event.keyCode;
      const hours = this.dateTimeAdapter.getHours(this._activeDate);

      if (keyCode === A && hours >= 12) {
         const date = this._activeDate;
         this.activeDate = this.dateTimeAdapter.createDateTime(
            this.dateTimeAdapter.getYear(date),
            this.dateTimeAdapter.getMonth(date),
            this.dateTimeAdapter.getDate(date),
            hours - 12,
            this.dateTimeAdapter.getMinutes(date),
            0
         );
         this.selectedChange.emit(this._activeDate);
      } else if (keyCode === P && hours < 12) {
         const date = this._activeDate;
         this.activeDate = this.dateTimeAdapter.createDateTime(
            this.dateTimeAdapter.getYear(date),
            this.dateTimeAdapter.getMonth(date),
            this.dateTimeAdapter.getDate(date),
            hours + 12,
            this.dateTimeAdapter.getMinutes(date),
            0
         );
         this.selectedChange.emit(this._activeDate);
      }
   }

   private getNumberFromKeyCode(keyCode: number): number {
      if (NUMPAD_ZERO <= keyCode && keyCode <= NUMPAD_NINE) {
         keyCode = keyCode - 48;
      }

      return parseInt(String.fromCharCode(keyCode), 10);
   }

   /**
    * @param obj The object to check.
    * @returns The given object if it is both a date instance and valid, otherwise null.
    */
   private getValidDateOrNull(obj: any): D | null {
      return this.dateTimeAdapter.isDateInstance(obj) && this.dateTimeAdapter.isValid(obj) ? obj : null;
   }
}
